import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, AlertCircle, DollarSign } from 'lucide-react';

const Payments = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingPayments, setPendingPayments] = useState([]);
  const [overduePayments, setOverduePayments] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTermModal, setShowTermModal] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [formData, setFormData] = useState({
    term_name: '',
    days: ''
  });

  useEffect(() => {
    fetchPaymentData();
  }, []);

  const fetchPaymentData = async () => {
    try {
      const [pendingRes, overdueRes, termsRes] = await Promise.all([
        api.get('/payments/pending'),
        api.get('/payments/overdue'),
        api.get('/payments/terms')
      ]);
      setPendingPayments(pendingRes.data);
      setOverduePayments(overdueRes.data);
      setPaymentTerms(termsRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch payment data');
      setLoading(false);
    }
  };

  const handleTermSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTerm) {
        await api.put(`/payments/terms/${editingTerm.id}`, formData);
        toast.success('Payment term updated successfully');
      } else {
        await api.post('/payments/terms', formData);
        toast.success('Payment term added successfully');
      }
      fetchPaymentData();
      resetTermForm();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save payment term');
    }
  };

  const handleEditTerm = (term) => {
    setEditingTerm(term);
    setFormData(term);
    setShowTermModal(true);
  };

  const handleDeleteTerm = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment term?')) {
      try {
        await api.delete(`/payments/terms/${id}`);
        toast.success('Payment term deleted successfully');
        fetchPaymentData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete payment term');
      }
    }
  };

  const markAsPaid = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}`, { payment_status: 'Paid' });
      toast.success('Payment marked as paid');
      fetchPaymentData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const resetTermForm = () => {
    setShowTermModal(false);
    setEditingTerm(null);
    setFormData({
      term_name: '',
      days: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return <div className="container">Loading payment data...</div>;
  }

  return (
    <div className="container">
      <h1 style={{ marginBottom: '30px' }}>Payments</h1>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          className={`btn ${activeTab === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending Payments
        </button>
        <button
          className={`btn ${activeTab === 'overdue' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('overdue')}
        >
          Overdue Payments
        </button>
        <button
          className={`btn ${activeTab === 'terms' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('terms')}
        >
          Payment Terms
        </button>
      </div>

      {activeTab === 'pending' && (
        <div className="card">
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
            <DollarSign size={20} style={{ marginRight: '8px' }} />
            Pending Payments
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Retailer</th>
                <th>Amount</th>
                <th>Payment Terms</th>
                <th>Due Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingPayments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.order_number}</td>
                  <td>{payment.retailer_name}</td>
                  <td>₹{payment.total_amount?.toFixed(2) || '0.00'}</td>
                  <td>{payment.term_name}</td>
                  <td>{payment.due_date ? new Date(payment.due_date).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <button
                      className="btn btn-primary"
                      onClick={() => markAsPaid(payment.id)}
                      style={{ fontSize: '12px', padding: '5px 10px' }}
                    >
                      Mark as Paid
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'overdue' && (
        <div className="card">
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', color: '#dc3545' }}>
            <AlertCircle size={20} style={{ marginRight: '8px' }} />
            Overdue Payments
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Retailer</th>
                <th>Amount</th>
                <th>Days Overdue</th>
                <th>Due Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {overduePayments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.order_number}</td>
                  <td>{payment.retailer_name}</td>
                  <td>₹{payment.total_amount?.toFixed(2) || '0.00'}</td>
                  <td>
                    <span className="badge badge-danger">
                      {Math.floor(payment.days_overdue)} days
                    </span>
                  </td>
                  <td>{payment.due_date ? new Date(payment.due_date).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <button
                      className="btn btn-primary"
                      onClick={() => markAsPaid(payment.id)}
                      style={{ fontSize: '12px', padding: '5px 10px' }}
                    >
                      Mark as Paid
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'terms' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>Payment Terms</h3>
            <button className="btn btn-primary" onClick={() => setShowTermModal(true)}>
              <Plus size={16} style={{ marginRight: '5px' }} />
              Add Payment Term
            </button>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Term Name</th>
                <th>Days</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paymentTerms.map(term => (
                <tr key={term.id}>
                  <td>{term.term_name}</td>
                  <td>{term.days} days</td>
                  <td>
                    <button
                      onClick={() => handleEditTerm(term)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px' }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteTerm(term.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showTermModal && (
        <div className="modal-overlay" onClick={resetTermForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingTerm ? 'Edit Payment Term' : 'Add Payment Term'}
              </h2>
            </div>

            <form onSubmit={handleTermSubmit}>
              <div className="form-group">
                <label className="form-label">Term Name *</label>
                <input
                  type="text"
                  name="term_name"
                  className="form-control"
                  value={formData.term_name}
                  onChange={handleChange}
                  placeholder="e.g., Net 30"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Days *</label>
                <input
                  type="number"
                  name="days"
                  className="form-control"
                  value={formData.days}
                  onChange={handleChange}
                  min="0"
                  required
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetTermForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTerm ? 'Update' : 'Add'} Payment Term
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;